import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Empty, Inline, Tag, money, when } from "@/components/backoffice/kit";

function Tile({ label, value, note, testid }) {
    return (
        <div className="border border-[#1a1a1a] bg-white p-3" data-testid={testid}>
            <p className="text-[12px] font-semibold uppercase text-[#4d4d4d]">{label}</p>
            <p className="text-[22px] font-semibold tabular-nums">{value}</p>
            {note && <p className="text-[13px] text-[#4d4d4d]">{note}</p>}
        </div>
    );
}

const PAYOUT = { paid: ["Paid", "green"], internal: ["Paid (house)", "green"], waiting_connect: ["Held — no Stripe yet", "red"], failed: ["Failed", "red"] };

/** Phase 9b: one number for the whole Flyers & Print section — NDF's live price plus this percent is 13thav's list price. */
function PrintMarkup() {
    const [m, setM] = useState(null);
    const [pct, setPct] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const load = useCallback(() => api.get("/print/markup").then(({ data }) => { setM(data); setPct(String(data.pct)); }).catch(() => setM({})), []);
    useEffect(() => { load(); }, [load]);
    if (m === null) return null;
    const save = () => {
        setSaving(true); setMsg("");
        api.put("/print/markup", { pct: Number(pct) })
            .then(({ data }) => { setMsg(`Saved — ${data.restamped} print products re-priced.`); load(); })
            .catch((e) => setMsg(formatApiError(e.response?.data?.detail) || "Could not save"))
            .finally(() => setSaving(false));
    };
    const refresh = () => {
        setSaving(true); setMsg("Pulling live prices from Next Day Flyers…");
        const poll = () => api.get("/print/refresh").then(({ data }) => {
            if (data.status === "running") { setTimeout(poll, 3000); return; }
            const r = data.result || {};
            setMsg(data.status === "done" ? `Pulled ${r.updated} of ${r.checked} prices from Next Day Flyers${r.failed?.length ? ` · ${r.failed.length} failed` : ""}.` : (r.error || "Could not refresh"));
            setSaving(false); load();
        }).catch((e) => { setMsg(formatApiError(e.response?.data?.detail) || "Could not refresh"); setSaving(false); });
        api.post("/print/refresh").then(() => setTimeout(poll, 3000))
            .catch((e) => { setMsg(formatApiError(e.response?.data?.detail) || "Could not refresh"); setSaving(false); });
    };
    return (
        <section className="border border-[#1a1a1a] bg-white p-3" data-testid="fees-print-markup">
            <h2 className="text-[16px] font-semibold">Flyers &amp; Print markup</h2>
            <p className="mt-1 text-[15px]">Every print price is Next Day Flyers' live price for that exact selection plus this percent. Resellers add their own markup on top.{m.ndf_pulled ? ` Last pulled ${m.ndf_pulled}.` : ""}</p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="grid gap-1 text-[14px] font-semibold">Markup %
                    <input type="number" inputMode="decimal" min="0" max="500" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)}
                        className="min-h-[44px] w-28 border border-[#1a1a1a] px-3 text-[16px] font-normal tabular-nums" data-testid="print-markup-input" />
                </label>
                <button type="button" onClick={save} disabled={saving || pct === ""} className="min-h-[44px] border border-[#1a1a1a] bg-[#1a1a1a] px-4 text-[15px] font-semibold text-white disabled:opacity-50" data-testid="print-markup-save">Save</button>
                <button type="button" onClick={refresh} disabled={saving} className="min-h-[44px] border border-[#1a1a1a] px-4 text-[15px] disabled:opacity-50" data-testid="print-markup-refresh">Re-pull NDF prices</button>
            </div>
            {msg && <p className="mt-2 text-[14px]" data-testid="print-markup-msg">{msg}</p>}
        </section>
    );
}

/** 8.12 Payouts & fees — read-only money facts: platform Stripe, consignment received, each shop's share, plan fees, history. */
export default function FeesScreen() {
    const [d, setD] = useState(null);
    const [err, setErr] = useState("");
    const load = useCallback(() => {
        setErr("");
        api.get("/admin/fees").then(({ data }) => setD(data)).catch((e) => { setD({}); setErr(formatApiError(e.response?.data?.detail)); });
    }, []);
    useEffect(() => { load(); }, [load]);
    if (d === null) return <div className="flex min-h-[30vh] items-center justify-center" data-testid="fees-loading"><Loader2 className="animate-spin" /></div>;
    const st = d.stripe || {};
    return (
        <div className="grid gap-4" data-testid="fees-screen">
            <h1 className="text-[22px] font-semibold">Payouts &amp; fees</h1>
            <Inline error={err} onRetry={load} testid="fees-error" />
            {d.consignment && (
                <>
                    <div className="border border-[#1a1a1a] bg-white p-3" data-testid="fees-stripe">
                        <Tag tone={st.charges_enabled ? "green" : "red"}>{st.charges_enabled ? "Platform Stripe · charges on" : "Platform Stripe · not ready"}</Tag>
                        <p className="mt-1 text-[15px]">{st.mode === "live" ? "Live mode." : "Test mode."} Card money for every consignment line and both house shops lands on this account.</p>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                        <Tile label="Consignment received" value={money(d.consignment.received)} note={`13thav's side of ${d.consignment.orders} consignment order${d.consignment.orders === 1 ? "" : "s"}`} testid="fees-received" />
                        <Tile label="Shop shares" value={money(d.consignment.shop_shares)} note="20% of standard + markup, owed to shops" testid="fees-shares" />
                        <Tile label="Card fees collected" value={money(d.card_fees)} note={`${d.fee_pct}% on card orders from connected shops`} testid="fees-card" />
                        <Tile label="Plan fees / month" value={money(d.plan_monthly)} note="Active and trial plans" testid="fees-plans" />
                    </div>
                    <PrintMarkup />
                    <section className="grid gap-2" data-testid="fees-shops">
                        <h2 className="text-[16px] font-semibold">Each shop's share</h2>
                        {(d.shops || []).length === 0 ? <Empty title="No consignment lines yet." /> : (
                            <ul className="divide-y divide-[#767676] border-y border-[#767676] text-[15px]">
                                {d.shops.map((s) => (
                                    <li key={s.slug} className="grid gap-x-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto]" data-testid={`fees-shop-${s.slug}`}>
                                        <span className="font-semibold">{s.name} <span className="font-normal text-[#4d4d4d]">· {s.lines} line{s.lines === 1 ? "" : "s"} · {money(s.standard)} standard</span></span>
                                        <span className="tabular-nums">Share {money(s.share)}</span>
                                        <span className="tabular-nums text-[#1e6b34]">Paid {money(s.paid)}</span>
                                        <span className={`tabular-nums ${s.upcoming > 0 ? "text-[#b3261e]" : "text-[#4d4d4d]"}`}>Upcoming {money(s.upcoming)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    <section className="grid gap-2" data-testid="fees-plan-list">
                        <h2 className="text-[16px] font-semibold">Plans</h2>
                        {(d.plans || []).length === 0 ? <Empty title="No paid plans." /> : (
                            <ul className="divide-y divide-[#767676] border-y border-[#767676] text-[15px]">
                                {d.plans.map((p) => (
                                    <li key={p.slug} className="flex flex-wrap justify-between gap-x-3 py-2">
                                        <span>{p.name} <span className="text-[#4d4d4d]">· {p.plan} · {p.status}</span></span>
                                        <span className="tabular-nums">{p.comped ? "comped" : `${money(p.monthly)}/mo`}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    <section className="grid gap-2" data-testid="fees-history">
                        <h2 className="text-[16px] font-semibold">History</h2>
                        {(d.history || []).length === 0 ? <Empty title="No consignment payouts yet." /> : (
                            <ul className="divide-y divide-[#767676] border-y border-[#767676] text-[15px]">
                                {d.history.map((h) => {
                                    const t = PAYOUT[h.status] || [h.status, "grey"];
                                    return (
                                        <li key={h.id} className="grid gap-x-3 py-2 sm:grid-cols-[150px_minmax(0,1fr)_auto_auto]">
                                            <span className="text-[#4d4d4d]">{when(h.created_at)}</span>
                                            <span>{h.store_name} · {h.order_number}</span>
                                            <span className="tabular-nums">{money(h.amount)}</span>
                                            <Tag tone={t[1]}>{t[0]}</Tag>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </>
            )}
        </div>
    );
}
